import { SearchPrefetchInterface } from '@1js/search-page-bootstrapper';
import { Injectable, NgZone, Optional } from '@angular/core';
import { LaunchResult } from '@office-start/launcher-types';
import { OfficeRouteItemType } from '@office-start/officehome-types';
import { ITokenProviderSpec, TokenProvider } from '@suiteux/suiteux-shell';
import { OfficeHomePageErrorHandler } from '../error-handler/error-handler.service';
import { ErrorId } from '../error-handler/error-id';
import { SearchConfigService } from '../search-results/services/search-config.service';
import { EncryptionService } from '../service-worker/encryption/encryption.service';
import { MSG_CRYPTO_API_NOT_AVAILABLE } from '../service-worker/encryption/web-crypto.service';
import { LinkTarget } from '../shared/constants/link-target.enum';
import { OfficeRoute } from '../shared/constants/office-route.enum';
import { KnownReferralPlatform } from '../shared/instrumentation/instrumentation.service';
import { FileItem, FileType } from '../shared/models/file-item.model';
import { WdOriginType } from '../shared/models/telemetry-constants.model';
import { DocumentInfo } from '../shared/models/wac-host.model';
import { Workload } from '../shared/models/workload.model';
import { AccessTokenConsumer, AccessTokenService } from '../shared/services/access-token.service';
import { AppLauncherService } from '../shared/services/app-launcher.service';
import { LocalAppLauncherService } from '../shared/services/local-app-launcher.service';
import { LocalStorageService } from '../shared/services/local-storage.service';
import { OfficeRouteService } from '../shared/services/office-route.service';
import { PeopleCardService } from '../shared/services/people-card.service';
import { SchedulePriority, SchedulerService } from '../shared/services/scheduler.service';
import { LaunchAction, UrlLauncherService } from '../shared/services/url-launcher.service';
import { WacContentService } from '../shared/services/wac-content.service';
import { WindowService } from '../shared/services/window.service';
import { WorkloadsService } from '../shared/services/workloads.service';
import { CapabilitiesService } from '../shared/shared-context/capabilities.service';
import { AuthType, ClientStartupConfigService } from '../shared/shared-context/client-startup-config.service';
import { ContextService } from '../shared/shared-context/context.service';
import { GuidGenerator } from '../shared/utilities/guid-generator.utility';
import { bytesToHex, hexToBytes } from '../shared/utilities/hex-string.utility';
import { UploadActionType, UploadLocalFileService } from '../upload/upload-local-file/upload-local-file.service';
import { FastSearchUXInstrumentationService } from './fast-searchux-instrumentation.service';
import { ICacheItemStats, ITokenProviderStats, IWrappedXMLHttpRequest, Prefetch, SearchPageBootstrapperService } from './search-page-bootstrapper/search-page-bootstrapper.service';
import { SearchPageInstrumentationService } from './search-page-instrumentation/search-page-instrumentation.service';

const ENCRYPTION_EDGE_17_OR_LOWER_ERROR_CODE = '80700011';
const MSG_ENCRYPTION_EDGE_SPARTAN_17_OR_LOWER = `Could not complete the operation due to error ${ENCRYPTION_EDGE_17_OR_LOWER_ERROR_CODE}.`;
@Injectable({
    providedIn: 'root'
})
export class SharedSearchService {
    public readonly sharePointTokenProvider: TokenProvider = this.createTokenProvider(AccessTokenConsumer.SharePoint);
    public readonly substrateSearchServiceTokenProvider: TokenProvider = this.createTokenProvider(AccessTokenConsumer.QueryFormulation);
    public readonly graphTokenProvider: TokenProvider = this.createTokenProvider(AccessTokenConsumer.Graph);

    private searchCenterUrl: string;

    private readonly searchCenterUrlKey: string = '-searchcenterurl';
    private readonly bingSuggestionsAppID: string = 'D41D8CD98F00B204E9800998ECF8427E0C818072';
    private readonly bingSuggestionsUrl: string = 'https://www.bing.com/as/init?pt=onedrive&clientid=';

    // A RegExp to differentiate Local files from Web files.
    private static readonly webRegex = RegExp('^https?:\/\/', 'i');

    /**
     * FAST Modules
     */

    // FAST Prefetch module.
    public readonly searchUXPrefetchModule: Promise<Prefetch> = new Promise(resolve => this.resolveSearchUXPrefetchModule = resolve);
    // Used to resolve the Prefetch module loaded Promise.
    private resolveSearchUXPrefetchModule: (prefetch: Prefetch) => void;

    private prefetchLibraryInitialized: boolean;

    constructor(
        private readonly accessTokenService: AccessTokenService,
        private readonly appLauncherService: AppLauncherService,
        private readonly capabilitiesService: CapabilitiesService,
        private readonly clientStartupConfigService: ClientStartupConfigService,
        private readonly contextService: ContextService,
        @Optional() private readonly encryptionService: EncryptionService,
        private readonly fastSearchUXInstrumentationService: FastSearchUXInstrumentationService,
        private readonly globalErrorHandler: OfficeHomePageErrorHandler,
        private readonly localAppLauncherService: LocalAppLauncherService,
        private readonly localStorageService: LocalStorageService,
        private readonly ngZone: NgZone,
        private readonly officeRouteService: OfficeRouteService,
        private readonly peopleCardService: PeopleCardService,
        private readonly schedulerService: SchedulerService,
        private readonly searchConfigService: SearchConfigService,
        private readonly uploadLocalFileService: UploadLocalFileService,
        private readonly windowService: WindowService,
        private readonly workloadsService: WorkloadsService,
        private readonly wacContentService: WacContentService,
        private readonly urlLauncherService: UrlLauncherService,
        private readonly searchPageInstrumentationService: SearchPageInstrumentationService,
        private readonly searchPageBootstrapperService: SearchPageBootstrapperService
    ) {
        if (!this.capabilitiesService.capabilities.showSearch) {
            return;
        }

        if (this.clientStartupConfigService.config.authType !== AuthType.MSA) {
            this.initializePrefetchLibrary();
        }

        this.setup();
    }

    // TODO: remove when FAST team adds a property without tags
    public static plainTextTitle(props: any): string {
        const displayText: string = props.displayText || props.title;
        return displayText ? displayText.replace('<mark>', '').replace('</mark>', '') : '';
    }

    public async warmup3S(graphRoot: string | undefined): Promise<IWrappedXMLHttpRequest & ITokenProviderStats & ICacheItemStats> {
        const searchCenterUrl = await this.getSearchCenterUrl(graphRoot);
        const prefetch = await this.searchUXPrefetchModule;

        return prefetch.warmupSubstrateSearch({
            tokenProvider: this.substrateSearchServiceTokenProvider,
            headers: {
                'x-client-language': this.contextService.sharedContext.officeMarket,
                'x-anchormailbox': this.getXAnchorMailbox()
            },
            postAction: this.searchConfigService.config.fastUseSubstrateEnabled ? 'initv2' : 'init',
            substrateUrl: prefetch.resolveSubstrateUrl(this.searchPageBootstrapperService.resolveLokiEnvironment()),
            queryStringParameters: {
                scenario: 'officehome',
                userContextUrl: searchCenterUrl === '/search' ? `${this.contextService.sharedContext.sharePointResourceId}/search` : searchCenterUrl
            }
        });
    }

    public async getSearchCenterUrl(graphRoot: string | undefined): Promise<string> {
        let refreshCache: boolean = false;
        // Fetch data from available cache but refresh required.
        if (!this.searchCenterUrl) {
            await this.getSearchCenterUrlFromCache();
            refreshCache = true;
        }
        // Data is not available in cache and fetching from backend and refreshing cache
        if (!this.searchCenterUrl) {
            await this.fetchSearchCenterUrlFromBackend(graphRoot);
            refreshCache = false;
        }
        // If the data fetched from cache then a fire non awaited async call for refresh cache
        if (refreshCache) {
            this.schedulerService.scheduleWork(() => {
                this.fetchSearchCenterUrlFromBackend(graphRoot);
            }, SchedulePriority.Low);
        }
        return this.searchCenterUrl;
    }

    public getXAnchorMailbox() {
        const clientConfig = this.clientStartupConfigService.config;
        if (clientConfig.puid && clientConfig.tenantId) {
            return `PUID:${clientConfig.puid}@${clientConfig.tenantId}`;
        }
        return clientConfig.userPrincipalName;
    }

    public createTokenProvider(tokenConsumer: AccessTokenConsumer): TokenProvider {
        return async (spec: ITokenProviderSpec) => {
            const { url, attempt } = spec;
            // Indicated that they tried the token already, and would like a
            // refreshed one (i.e. the token they used probably expired).
            const isRetry = attempt === 1;
            const resource = tokenConsumer === AccessTokenConsumer.SharePoint ? (url ?? this.contextService.sharedContext.sharePointResourceId) : url;
            const tokenResponse = await this.accessTokenService
                .getAccessToken(tokenConsumer, isRetry, resource);
            return tokenResponse.success
                ? Promise.resolve(tokenResponse.token)
                : Promise.reject(tokenResponse.error);
        };
    }

    public async initializePrefetchLibrary(): Promise<void> {
        if (this.prefetchLibraryInitialized) {
            return;
        }

        const prefetch: Prefetch = await this.loadPrefetch();

        /**
         * This flag is set before the call to prefetch.init in order to
         * avoid any yielding of the thread and potentially ending up
         * with calling the init function more than once.
         */
        this.prefetchLibraryInitialized = true;

        // Needs to be set before all warmup calls.
        prefetch.init({
            puid: this.clientStartupConfigService.config.puid,
            cache: {
                createdBy: 'OfficeHome',
                createdAt: 0,
                participants: [],
                caches: {}
            }
        });

        this.resolveSearchUXPrefetchModule(prefetch);
    }

    public setAppsAutosuggestClick(props: any, e: any, thirdPartyApps: Array<Workload>): void {
        this.ngZone.run(() => {
            props.onClick(); // Important for telemetry.

            let workloadWithMatchingId: Workload;

            //Add app to workloads.service list of used third party apps if the app is a third party app
            if (thirdPartyApps && thirdPartyApps.length > 0) {
                workloadWithMatchingId = thirdPartyApps.find(workload => workload.Id.toLowerCase() === props.id.toLowerCase());
                if (workloadWithMatchingId) {
                    this.workloadsService.updateUsedThirdPartyApps(workloadWithMatchingId);
                }
            }

            const nameDetail = workloadWithMatchingId ?
                'ConfigurationDrivenSearchBox.ThirdPartyAppSuggestion.onClick' :
                'ConfigurationDrivenSearchBox.ProvisionedAppSuggestion.onClick';
            let referral = props.referral || props.id;
            // Provisioned apps have 'shell' at the front of the id needed to log referral
            if (referral && referral.toLocaleLowerCase().startsWith('shell')) {
                referral = referral.replace(/shell/i, '');
            }

            let clickLogged = false;
            let logAppClick;

            if (props.customLoggingFunction) {
                logAppClick = props.customLoggingFunction;
            } else {
                logAppClick = (success: boolean, referralPlatform?: KnownReferralPlatform, pageNav?: boolean) => {
                    if (clickLogged) {
                        return;
                    }
                    clickLogged = true;

                    this.workloadsService.pinWorkload(workloadWithMatchingId);

                    this.fastSearchUXInstrumentationService.searchLogger(
                        {
                            eventType: 'USAGE',
                            name: 'engagement',
                            nameDetail,
                            properties: {
                                eventData: ''
                            }
                        },
                        success ? referral : '',
                        referralPlatform,
                        pageNav
                    );
                };
            }

            if (workloadWithMatchingId) {
                logAppClick(true, KnownReferralPlatform.Web); // Assumes all 3rd party apps are web apps
                if (this.capabilitiesService.capabilities.contentLauncherEnabled) {
                    import('@office-start/launcher-service').then(
                        launchSvc => {
                            launchSvc.launchApp({ action: 'OpenAutosuggestApp', event: e, appName: workloadWithMatchingId.Text, url: workloadWithMatchingId.Url, useUrl: true});
                        }
                    );
                } else {
                    this.appLauncherService.launchApp(workloadWithMatchingId, { ...e }, LaunchAction.OpenAutosuggestApp);
                }

                return;
            }

            // Only wait to log click if we open in a new tab, otherwise we risk losing the event
            if (this.contextService.sharedContext.defaultLinkTarget !== LinkTarget.NewTab) {
                logAppClick(true, KnownReferralPlatform.Web);
            }
            //As part of refactoring and making one appLaunch class, this is used to
            //create appDetails and pass to appLauncherservice
            const openWebApp = () => {
                if (props.providerId && (props.providerId === 'MetaOsAppSuggestionProvider' || props.providerId === 'CopilotAppSuggestionProvider') && props.id) {
                    import('@office-start/launcher-service').then(
                        launchSvc => {
                            launchSvc.launchUrl({ route: OfficeRoute.M365Apps, appId: props.id, type: OfficeRouteItemType.Mos }, {action: 'OpenAutosuggestApp'}).then(() => {
                                logAppClick(true, KnownReferralPlatform.Web);
                            }, () => {
                                logAppClick(false);
                            });
                        }
                    );
                    return;
                } else if (this.capabilitiesService.capabilities.contentLauncherEnabled) {
                    import('@office-start/launcher-service').then(
                        launchSvc => {
                            launchSvc.launchUrl(props.url, {action: 'OpenAutosuggestApp'}).then(() => {
                                logAppClick(true, KnownReferralPlatform.Web);
                            }, () => {
                                logAppClick(false);
                            });
                        }
                    );
                } else {
                    this.urlLauncherService.openUrl(props.url, LaunchAction.OpenAutosuggestApp).then(() => {
                        logAppClick(true, KnownReferralPlatform.Web);
                    }, () => {
                        logAppClick(false);
                    });
                }
            };

            Promise.all([this.workloadsService.getWorkloads(), this.workloadsService.getAdditionalWorkloads()]).then(workloadArray => {
                const workloads = workloadArray[0].concat(workloadArray[1]);
                const appId = props.id.startsWith('Shell') ? props.id : 'Shell' + props.id;
                workloadWithMatchingId = workloads.find(workload => workload.Id.toLowerCase() === appId.toLowerCase());
                if (!workloadWithMatchingId) {
                    openWebApp();
                    return;
                }

                if (this.capabilitiesService.capabilities.contentLauncherEnabled) {
                    import('@office-start/launcher-service').then(
                        launchSvc => {
                            // This logic is the same as what used to be done in 'launchApp'.
                            // It opens inAppNav + FileType items inline, but others are attempted to be opened in native
                            // and then fall back to web.
                            // $TODO: launchApp's role should be decided. If it should handle this scenario it should be fixed.
                            // Currently, it always attempts to open a native app.
                            let launchPromise: Promise<LaunchResult>;
                            if (workloadWithMatchingId.InAppNav && workloadWithMatchingId.FileType) {
                                launchPromise = launchSvc.launchUrl({
                                    route: 'Start',
                                    type: OfficeRouteItemType.Base,
                                    app: FileType[workloadWithMatchingId.FileType]
                                }, { action: 'OpenAutosuggestApp'});
                            } else {
                                launchPromise = launchSvc.launchApp({ action: 'OpenAutosuggestApp', event: e, appName: workloadWithMatchingId.Text, url: workloadWithMatchingId.Url });
                            }
                            launchPromise
                            .then(result => {
                                const platform = result.result === 'Native' ? KnownReferralPlatform.Native : KnownReferralPlatform.Web;
                                // NOTE: This assumes autosuggest apps open in separate tabs. If this changes we would need to log before launching.
                                logAppClick(!!result, platform, result.result === 'StartPage');
                            });
                        }
                    );
                } else {
                    this.appLauncherService.launchApp(workloadWithMatchingId, { ...e }, LaunchAction.OpenAutosuggestApp, true).then(result => {
                        const platform = result === 'Native' ? KnownReferralPlatform.Native : KnownReferralPlatform.Web;
                        // NOTE: This assumes autosuggest apps open in separate tabs. If this changes we would need to log before launching.
                        logAppClick(!!result, platform, result === 'StartPage');
                    });
                }

            }, () => {
                openWebApp();
            });
        });
    }

    public setFilesAutosuggestClick(props: any): void {
        this.ngZone.run(() => {
            props.onClick(); // Important for telemetry.
            let url: string = props.url;

            const fileType = FileItem.fileTypeFromExtension(props.fileExtension);
            const isLocalFile = !SharedSearchService.webRegex.test(url);
            const referral = FileItem.referralFromFileType(fileType);
            const wdOrigin = this.contextService.getWdOrigin(WdOriginType.Search);
            let clickLogged = false;

            let logFileClick;

            if (props.customLoggingFunction) {
                logFileClick = props.customLoggingFunction;
            } else {
                logFileClick = (success: boolean, referralPlatform?: KnownReferralPlatform) => {
                    if (clickLogged) {
                        return;
                    }
                    clickLogged = true;
                    this.fastSearchUXInstrumentationService.searchLogger(
                        {
                            eventType: 'USAGE',
                            name: 'engagement',
                            nameDetail: 'FileClickedWithExtensionCustom',
                            properties: {
                                eventData: ''
                            }
                        },
                        success ? referral : '',
                        referralPlatform
                    );
                };
            }

            // Sways have an unknown file type here
            // TODO: have props contain the file type.
            // TODO: Need to add similar query params for clicks in search results view.
            if (fileType !== FileType.Unknown && fileType !== FileType.None && !isLocalFile && !props.shortUrl) {
                const clickTime = Date.now();
                const clickId = GuidGenerator.createGuid();
                url = `${url}&ct=${clickTime}&wdOrigin=${wdOrigin}&cid=${clickId}`;
            }
            // Only wait to log click if we open in a new tab, otherwise we risk losing the event
            const wacHostEnabled = this.capabilitiesService.wacHostEnabledForFileType(fileType);
            const openWebApp = () => {

                if (wacHostEnabled) {
                    const documentInfo: DocumentInfo = {
                        docUrl: url,
                        fileName: props.displayTextRaw,
                        fileType,
                        wdOrigin
                    };

                    logFileClick(true, KnownReferralPlatform.Web);
                    this.wacContentService.bootstrapAndRoute(documentInfo);
                } else {
                    if (this.contextService.sharedContext.defaultLinkTarget !== LinkTarget.NewTab) {
                        logFileClick(true, KnownReferralPlatform.Web);
                    }
                    if (this.capabilitiesService.capabilities.contentLauncherEnabled) {
                        import('@office-start/launcher-service').then(
                            launchSvc => {
                                launchSvc.launchUrl(url, {action: 'OpenAutosuggestFile'}).then(() => {
                                    logFileClick(true, KnownReferralPlatform.Web);
                                }, () => {
                                    logFileClick(false);
                                });
                            }
                        );
                    } else {
                        this.urlLauncherService.openUrl(url, LaunchAction.OpenAutosuggestFile).then(() => {
                            logFileClick(true, KnownReferralPlatform.Web);
                        }, () => {
                            logFileClick(false);
                        });
                    }
                }
            };

            if (this.capabilitiesService.capabilities.contentLauncherEnabled) {
                import('@office-start/launcher-service').then(
                    launchSvc => {
                        launchSvc.launchApp({ action: 'OpenDocument', appName: FileType[fileType], url: props.canonicalUrl || url, useUrl: true})
                        .then(result => {
                            if (result && result.success) {
                                logFileClick(true, KnownReferralPlatform.Native);
                                return;
                            }
                            if (isLocalFile) {
                                const title = SharedSearchService.plainTextTitle(props);
                                this.uploadLocalFileService.showModal(url, title, UploadActionType.Open);
                                logFileClick(false);
                            } else {
                                openWebApp();
                            }
                        }).catch(() => {
                            openWebApp();
                        });
                    }
                );
            } else {
                //Take the user to the file
                this.localAppLauncherService.launchOfficeApp(FileType[fileType], props.canonicalUrl || url).then(success => {
                    if (success) {
                        logFileClick(true, KnownReferralPlatform.Native);
                        return;
                    }
                    if (isLocalFile) {
                        const title = SharedSearchService.plainTextTitle(props);
                        this.uploadLocalFileService.showModal(url, title, UploadActionType.Open);
                        logFileClick(false);
                    } else {
                        openWebApp();
                    }
                }).catch(() => {
                    openWebApp();
                });
            }
        });
    }

    public setPersonAutosuggestClick(props: any, peopleCardContainer: HTMLDivElement): void {
        // Run in angular zone to help change detection
        this.ngZone.run(() => {
            props.onClick(); // Important for telemetry.

            const displayText = SharedSearchService.plainTextTitle(props);
            const idIsInAadFormat = props.id.includes('@');
            const upnIsInEmailFormat = displayText && displayText.indexOf('@') > 0;
            const peopleCardMinScreenWidth = 900;

            if (this.peopleCardService && (upnIsInEmailFormat || idIsInAadFormat)) {
                const aadObjectId = props.id.split('@')[0];
                const upn = upnIsInEmailFormat ? displayText : '';
                const openInExtendedView = this.windowService.innerWidth > peopleCardMinScreenWidth;

                this.peopleCardService.addPersonaCardElement(peopleCardContainer, aadObjectId, upn, openInExtendedView).then(() => {
                    const peopleCardElement = document.getElementById('personCardClickElement');
                    setTimeout(() => {
                        peopleCardElement.click();
                    }, 500);
                });
            } else if (displayText) {
                this.officeRouteService.navigateTo({ route: OfficeRoute.Search }, false, { q: displayText });
            }
        });
    }

    public async setup() {
        const instrumenter = await this.searchPageInstrumentationService.searchUXInstrumenter;

        if (this.clientStartupConfigService.config.authType === AuthType.MSA && this.searchConfigService.config.fastODCFileProviderEnabled) {
            const clientConfig = this.clientStartupConfigService.config;
            const script = document.createElement('script');
            script.src = this.bingSuggestionsUrl + clientConfig.puid.toString();
            document.head.appendChild(script);

            instrumenter.setProps({
                bingConsumerApiContext: {
                    'appId': this.bingSuggestionsAppID,
                    'viewerId': clientConfig.puid,
                    'fromPhotos': false
                }
            });
        }

        instrumenter.markAppBoot();
    }

    private handleError(error: Error, errorId: ErrorId): void {
        if (!error) {
            return;
        }

        // Don't log the "Crypto API not available" (from legacy browsers, mostly IE11)
        if (error.message === MSG_CRYPTO_API_NOT_AVAILABLE) {
            return;
        }

        // Don't log the encryption exceptions thrown from Edge Spartan 17 or lower.
        // Details here: https://stackoverflow.com/questions/33043091/public-key-encryption-in-microsoft-edge
        if (error.message === MSG_ENCRYPTION_EDGE_SPARTAN_17_OR_LOWER || error.message.indexOf(ENCRYPTION_EDGE_17_OR_LOWER_ERROR_CODE) >= 0) {
            return;
        }

        this.globalErrorHandler.handleError(error, errorId);
    }

    private async getSearchCenterUrlFromCache(): Promise<void> {
        const searchCenterUrl: string = this.localStorageService.get(this.searchCenterUrlKey);
        if (searchCenterUrl) {
            if (this.encryptionService) {
                try {
                    this.searchCenterUrl = await this.encryptionService.decryptData<string>(hexToBytes(searchCenterUrl));
                } catch (error) {
                    this.handleError(error, ErrorId.EaiTE);
                }
            } else {
                this.searchCenterUrl = searchCenterUrl;
            }
        }
    }

    private async fetchSearchCenterUrlFromBackend(_graphRoot: string | undefined): Promise<void> {
        const prefetch = await this.searchUXPrefetchModule;
        type currentFunc = SearchPrefetchInterface['fetchSearchCenterUrl'];
        type currentOptions = Parameters<currentFunc>[0];
        type newFunc = (o: currentOptions & { graphTokenProvider: TokenProvider, graphRoot: string | undefined, environment: unknown }) => ReturnType<currentFunc>;
        const fetchSearchCenterUrl: newFunc = prefetch.fetchSearchCenterUrl;

        this.searchCenterUrl = await fetchSearchCenterUrl({
            siteRoot: this.contextService.sharedContext.sharePointResourceId,
            tokenProvider: this.sharePointTokenProvider,
            environment: this.searchPageBootstrapperService.resolveLokiEnvironment(),
            graphTokenProvider: this.graphTokenProvider,
            graphRoot: undefined // TODO: Use graphRoot when fetchSearchCenterUrl is fixed
        }).catch(() => `${this.contextService.sharedContext.sharePointResourceId}`);

        const saveUrlToStorage = (url: string) => this.localStorageService.set(this.searchCenterUrlKey, url);

        if (this.encryptionService) {
            try {
                const encryptedSearchCenterUrl = await this.encryptionService.encryptData<string>(this.searchCenterUrl);
                const encryptedHexSearchCenterUrl = bytesToHex(new Uint8Array(encryptedSearchCenterUrl));
                saveUrlToStorage(encryptedHexSearchCenterUrl);
            } catch (error) {
                this.handleError(error, ErrorId.SmMcK);
            }
        } else {
            saveUrlToStorage(this.searchCenterUrl);
        }
    }

    private async loadPrefetch(): Promise<Prefetch> {
        const searchPageModule = await this.searchPageBootstrapperService.searchPageModule;

        return await searchPageModule.loadPrefetch().then(prefetch => prefetch);
    }
}
